import { useEffect, useSyncExternalStore } from "react";
import { fetchInternalConversations } from "../../api/internalMessages";

/**
 * "Mensajes de FORJA sin leer" para el flujo Jugador de la PWA.
 *
 * Por qué existe (02-11-2026): FORJA le escribió a una persona desde el panel
 * y en el teléfono no se veía nada. El buzón estaba detrás del cajón, en una
 * fila que decía "Enviar comentarios" —el nombre habla de ESCRIBIR, así que
 * nadie la toca para LEER— y sin ningún contador. El único canal que avisó fue
 * el mail.
 *
 * Gemelo de `features/jugador/MensajesForjaStore.kt` en Android, con el mismo
 * reparto: el número lo descubre esta store, pero quien lo baja a cero es el
 * buzón al abrir la conversación, porque es ahí donde el server marca leído.
 * Con dos contadores sueltos el aviso queda pegado después de leer.
 *
 * El inbox del organizador NO usa esto: tiene el suyo en Layout.tsx, que es
 * otra pantalla y otro lugar.
 */

let sinLeer = 0;
let ultimoMs = 0;
let pidiendo = false;
const oyentes = new Set<() => void>();

const TTL_MS = 5 * 60_000;

function avisar() {
  for (const oyente of oyentes) oyente();
}

/** Número exacto recién sabido (lo publica también el buzón al leer). */
export function publicarMensajesForja(cantidad: number) {
  const valor = Math.max(0, cantidad);
  if (valor === sinLeer) return;
  sinLeer = valor;
  avisar();
}

/** Logout: que la próxima cuenta no herede el contador de la anterior. */
export function resetMensajesForja() {
  sinLeer = 0;
  ultimoMs = 0;
  avisar();
}

/**
 * Consulta el inbox. Con TTL porque lo llama cada pantalla que muestra el
 * número; se traga el error a propósito — un contador que no se pudo
 * actualizar no es motivo para molestar a nadie.
 */
export async function refrescarMensajesForja(forzar = false) {
  const ahora = Date.now();
  if (!forzar && (pidiendo || ahora - ultimoMs < TTL_MS)) return;
  pidiendo = true;
  try {
    const respuesta = await fetchInternalConversations();
    publicarMensajesForja(respuesta.conversations.reduce((total, c) => total + c.unreadCount, 0));
    ultimoMs = Date.now();
  } catch {
    // Sin red, sin sesión, lo que sea: queda el último número conocido.
  } finally {
    pidiendo = false;
  }
}

export function useMensajesForjaSinLeer(): number {
  const valor = useSyncExternalStore(
    (alCambiar) => {
      oyentes.add(alCambiar);
      return () => {
        oyentes.delete(alCambiar);
      };
    },
    () => sinLeer,
    () => 0,
  );

  useEffect(() => {
    void refrescarMensajesForja();
    // Volver a la pestaña es justo cuando puede haber algo nuevo. En la PWA
    // instalada esto es el equivalente del "volver a la app".
    const alVolver = () => {
      if (document.visibilityState === "visible") void refrescarMensajesForja(true);
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
  }, []);

  return valor;
}
